import { useState } from 'react';
import { AuthSession } from '../auth/auth.schemas';
import { Icon } from '../../shared/Icon';
import { TELEGRAM_LINKS } from '../../shared/links';
import { faqs, features, platforms, servers, steps } from './landing.data';
import './LandingPage.css';

type LandingPageProps = {
  isAuthenticated: boolean;
  session: AuthSession | null;
  onLogout: () => void;
  onOpenCabinet: () => void;
};

const landingSubscriptionPlans = [
  {
    title: '⚡ Неделя - Start (7 дней)',
    badge: 'Быстрый старт',
    price: '45 ₽',
    text: 'Быстрый доступ к серверам и подключению на ваших устройствах.',
  },
  {
    title: '🗓️ Месяц - Standard (30 дней)',
    badge: 'Лучшее предложение',
    price: '99 ₽',
    text: 'Оптимальная подписка для регулярного доступа ко всем серверам MifVPN.',
    highlighted: true,
  },
  {
    title: '💎 Год - Premium (365 дней)',
    badge: 'Долгий доступ',
    oldPrice: '1200 ₽',
    price: '999 ₽',
    text: 'Долгий доступ без лишних продлений и тот же быстрый UX на ваших устройствах.',
  },
];

function getPlatformClassName(name: string) {
  const normalizedName = name.toLowerCase();

  if (normalizedName.includes('iphone') || normalizedName.includes('ipad')) {
    return 'apple';
  }

  if (normalizedName.includes('android')) {
    return 'android';
  }

  if (normalizedName.includes('windows')) {
    return 'windows';
  }

  if (normalizedName.includes('linux')) {
    return 'linux';
  }

  return '';
}

function PlatformLogo({ name }: { name: string }) {
  const platform = getPlatformClassName(name);
  const common = {
    className: 'landing-platform-svg',
    width: 28,
    height: 28,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  switch (platform) {
    case 'apple':
      return (
        <svg {...common}>
          <path d="M15.5 4.1c-.9.5-1.6 1.5-1.5 2.6 1-.1 2-.7 2.6-1.5.6-.8.9-1.8.8-2.7-.7 0-1.4.4-1.9.8v.8Z" fill="currentColor" />
          <path d="M18.5 16.1c-.4.9-.6 1.3-1.1 2.1-.7 1.1-1.7 2.5-2.9 2.5-1.1 0-1.4-.7-2.8-.7s-1.8.7-2.9.7c-1.2 0-2.1-1.3-2.8-2.4-1.9-3-2.1-6.5-.9-8.4.8-1.4 2.2-2.2 3.5-2.2s2.2.7 3.3.7c1 0 1.7-.7 3.2-.7 1.1 0 2.3.6 3.1 1.7-2.7 1.5-2.3 5.4.3 6.7Z" fill="currentColor" />
        </svg>
      );
    case 'android':
      return (
        <svg {...common}>
          <path d="M7.2 9.5h9.6v7.2c0 1-.8 1.8-1.8 1.8H9c-1 0-1.8-.8-1.8-1.8V9.5Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="m8.4 5.4 1.2 2M15.6 5.4l-1.2 2M7 9.5c.5-2.2 2.4-3.7 5-3.7s4.5 1.5 5 3.7M5.2 10.4v4.8M18.8 10.4v4.8M9.8 18.4v2M14.2 18.4v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M10 9.2h.01M14 9.2h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case 'windows':
      return (
        <svg {...common}>
          <path d="M4 5.4 10.8 4.5v7H4v-6.1ZM12.3 4.3 20 3.2v8.3h-7.7V4.3ZM4 12.9h6.8v6.7L4 18.7v-5.8ZM12.3 12.9H20v7.9l-7.7-1v-6.9Z" fill="currentColor" />
        </svg>
      );
    case 'linux':
      return (
        <svg {...common}>
          <path d="M8 18.8h8M7.5 15.8c.2-5.1 1.4-10.4 4.5-10.4s4.3 5.3 4.5 10.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M9 11.2h6M9.5 15.8h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M10.2 8.3h.01M13.8 8.3h.01" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
      );
    default:
      return <Icon name="device" />;
  }
}

export function LandingPage({ isAuthenticated, session, onLogout, onOpenCabinet }: LandingPageProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  const openCabinet = () => {
    closeMenu();
    onOpenCabinet();
  };

  return (
    <main className="landing">
      <div className="background-glow" />

      <header className="topbar" aria-label="Главная навигация">
        <a className="brand" href="#top" aria-label="MifVPN" onClick={closeMenu}>
          <span className="brand-mark">M</span>
          <span>MifVPN</span>
        </a>

        <nav className="desktop-nav">
          <a href="#benefits">Преимущества</a>
          <a href="#how">Как работает</a>
          <a href="#platforms">Платформы</a>
          <a href="#pricing">Тарифы</a>
        </nav>

        <div className="nav-actions">
          {isAuthenticated ? (
            <>
              <span className="session-pill" title={session?.email}>{session?.email}</span>
              <button className="button button-small" type="button" onClick={openCabinet}>Кабинет</button>
              <button className="link-button" type="button" onClick={onLogout}>Выйти</button>
            </>
          ) : (
            <button className="button button-small" type="button" onClick={openCabinet}>Открыть</button>
          )}
        </div>

        <button
          className="menu-button"
          type="button"
          aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          onClick={() => setIsMenuOpen((value) => !value)}
        >
          <Icon name="menu" />
        </button>

        <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`} id="mobile-menu">
          <a href="#benefits" onClick={closeMenu}>Преимущества</a>
          <a href="#how" onClick={closeMenu}>Как работает</a>
          <a href="#platforms" onClick={closeMenu}>Платформы</a>
          <a href="#pricing" onClick={closeMenu}>Тарифы</a>
          <div className="mobile-menu-actions">
            {isAuthenticated ? (
              <>
                <span className="session-pill mobile" title={session?.email}>{session?.email}</span>
                <button className="button button-primary" type="button" onClick={openCabinet}>Кабинет</button>
                <button className="button button-secondary" type="button" onClick={onLogout}>Выйти</button>
              </>
            ) : (
              <button className="button button-primary" type="button" onClick={openCabinet}>Открыть MifVPN</button>
            )}
          </div>
        </div>
      </header>

      <section className="hero section-shell" id="top">
        <div className="hero-copy">
          <div className="eyebrow">
            <Icon name="shield" />
            Быстрый приватный доступ без лишней настройки
          </div>
          <h1>MifVPN — быстрый и стабильный VPN</h1>
          <p className="hero-text">
            MifVPN — VPN-сервис для стабильного и безопасного подключения. Подключайтесь через сайт или Telegram-бота,
            управляйте подпиской, устройствами и серверами в личном кабинете. Новые пользователи могут активировать
            пробный период и подключить VPN в один клик.
          </p>

          <div className="hero-actions">
            <button className="button button-primary" type="button" onClick={openCabinet}>
              Открыть MifVPN
              <Icon name="arrow" />
            </button>
            <a className="button button-secondary" href="#pricing">Смотреть тарифы</a>
          </div>

          <div className="hero-stats" aria-label="Ключевые параметры">
            <div>
              <strong>⚡️</strong>
              <span>быстрый старт</span>
            </div>
            <div>
              <strong>3</strong>
              <span>устройства</span>
            </div>
            <div>
              <strong>∞</strong>
              <span>трафик</span>
            </div>
          </div>
        </div>

        <div className="hero-panel" aria-label="Превью интерфейса MifVPN">
          <div className="phone-preview">
            <div className="phone-head">
              <span>MifVPN</span>
              <span className="status-dot">Активна</span>
            </div>
            <div className="notice-card">
              <span>Доступен сервер Финляндия</span>
              <button aria-label="Закрыть уведомление">x</button>
            </div>
            <div className="subscription-card">
              <div className="card-row">
                <span className="muted">Подписка</span>
                <span className="pill pill-green">Активна</span>
              </div>
              <h3>Год - Premium</h3>
              <p>Активна до: 20.01.2027</p>
              <p>Устройства: 3/3</p>
            </div>
            <button className="connect-button" type="button" onClick={openCabinet}>Подключиться</button>
            <div className="mini-grid">
              <div>
                <Icon name="lock" />
                <span>Защищено</span>
              </div>
              <div>
                <Icon name="wifi" />
                <span>31 ms</span>
              </div>
            </div>
          </div>

          <div className="dashboard-preview">
            <div className="preview-card threat">
              <Icon name="shield" />
              <span>Always-on threat blocking</span>
            </div>
            <div className="preview-card route">
              <small>Live status</small>
              <strong>Protected via Finland</strong>
              <span>47 ms</span>
            </div>
            <div className="preview-card stream">
              <Icon name="zap" />
              <span>Streaming stays smooth</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-block" id="benefits">
        <div className="section-heading">
          <span className="eyebrow compact">Почему MifVPN ощущается проще</span>
          <h2>Все важное видно сразу: статус, устройства, тариф и подключение.</h2>
        </div>
        <div className="feature-grid">
          {features.map((feature) => (
            <article className="feature-card" key={feature.title}>
              <div className="icon-box">
                <Icon name={feature.icon} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell flow-section" id="how">
        <div className="flow-title">
          <span className="eyebrow compact">Как это работает</span>
          <h2>От оплаты до подключения в четыре спокойных шага.</h2>
        </div>
        <div className="steps">
          {steps.map((step, index) => (
            <div className="step" key={step}>
              <span>{index + 1}</span>
              <strong>{step}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="section-shell split-section" id="platforms">
        <div className="section-heading">
          <span className="eyebrow compact">Платформы</span>
          <h2>Подключение с телефона, ноутбука или рабочего компьютера.</h2>
          <p>На отдельной странице будет focused flow: определение платформы, клиент, конфиг и быстрые действия.</p>
        </div>
        <div className="platform-grid">
          {platforms.map((platform) => (
            <article className={`platform-card ${getPlatformClassName(platform.name)}`} aria-label={`${platform.name}: ${platform.hint}`} key={platform.name}>
              <span className="landing-platform-icon">
                <PlatformLogo name={platform.name} />
              </span>
              <strong>{platform.name}</strong>
              <span className="platform-hint">{platform.hint}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell servers-section">
        <div className="section-heading center">
          <span className="eyebrow compact">Серверы и сервисы</span>
          <h2>Быстрые направления и понятный статус доступности.</h2>
        </div>
        <div className="servers-card">
          <div className="server-grid">
            {servers.map((server) => (
              <div className={`server-tile ${server.tone}`} key={server.code}>
                <span className="server-tile-icon" aria-hidden="true">{server.icon}</span>
                <strong>{server.code}</strong>
                <span>{server.label}</span>
                {server.latency ? <small>{server.latency}</small> : null}
              </div>
            ))}
          </div>
          <div className="benefit-pills">
            <span><Icon name="lock" /> Простое подключение после оплаты</span>
            <span><Icon name="zap" /> Быстрая смена локации</span>
            <span><Icon name="device" /> До 3-х устройств</span>
            <span><Icon name="wifi" /> Доступ к нужным сервисам</span>
          </div>
        </div>
      </section>

      <section className="section-shell promo-grid">
        <article className="promo-card trial-card">
          <div className="icon-box amber">
            <Icon name="gift" />
          </div>
          <h2>3 дня бесплатно для старта</h2>
          <p>Запустите бота, подпишитесь на канал и проверьте trial прямо в кабинете.</p>
          <a className="button button-secondary warm" href={TELEGRAM_LINKS.bot} target="_blank" rel="noreferrer">Открыть бота</a>
        </article>

        <article className="promo-card referral-card">
          <div className="icon-box violet">
            <Icon name="users" />
          </div>
          <h2>Новая реферальная система</h2>
          <p>Мы готовим для вас лучшее предложение: понятные условия, удобные начисления и больше пользы за приглашения.</p>
          <div className="fake-input">Скоро вернемся с хорошими новостями</div>
        </article>
      </section>

      <section className="section-shell pricing-preview" id="pricing">
        <div className="section-heading">
          <span className="eyebrow compact">Тарифы</span>
          <h2>Простые подписки без перегруженной таблицы.</h2>
        </div>
        <div className="pricing-grid">
          {landingSubscriptionPlans.map((plan) => (
            <article className={`price-card ${plan.highlighted ? 'highlighted' : ''}`} key={plan.title}>
              <h3>{plan.title}</h3>
              <div className="landing-plan-price">
                {plan.oldPrice ? <s>{plan.oldPrice}</s> : null}
                <strong>{plan.price}</strong>
              </div>
              <span className="price-label">{plan.badge}</span>
              <p>{plan.text}</p>
              <button className={plan.highlighted ? 'button button-primary' : 'button button-secondary'} type="button" onClick={openCabinet}>Оформить</button>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell cta-panel">
        <h2>Меняйте локацию и пользуйтесь нужными сервисами без лишней возни.</h2>
        <button className="button button-primary" type="button" onClick={openCabinet}>Открыть MifVPN</button>
      </section>

      <section className="section-shell faq-section">
        <div className="faq-intro">
          <span className="eyebrow compact">FAQ</span>
          <h2>Вопросы перед стартом</h2>
          <p>Поддержка, инструкции и документы будут вынесены в отдельную страницу помощи.</p>
        </div>
        <div className="faq-list">
          {faqs.map((faq) => (
            <details className="faq-item" key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="footer section-shell">
        <a className="brand" href="#top" aria-label="MifVPN">
          <span className="brand-mark">M</span>
          <span>MifVPN</span>
        </a>
        <div>
          <a href={TELEGRAM_LINKS.support} target="_blank" rel="noreferrer">Поддержка</a>
          <a href={TELEGRAM_LINKS.channel} target="_blank" rel="noreferrer">Канал</a>
          <a href="#docs">Документы</a>
          <a href="#privacy">Политика данных</a>
        </div>
      </footer>
    </main>
  );
}
